import React from 'react';
import { Icon } from '@iconify/react';
import type { CashflowAccount } from '@/types/cashflow';
import { formatRupiahPlain, formatShortDateTime } from '@/utils/cashflowUtils';

interface CashflowPositionCardProps {
  accounts: CashflowAccount[];
  totalCash: number;
  isLoading?: boolean;
  onAddAccount?: () => void;
  onEditAccount?: (account: CashflowAccount) => void;
  onDeleteAccount?: (account: CashflowAccount) => void;
}

export const CashflowPositionCard: React.FC<CashflowPositionCardProps> = ({
  accounts,
  totalCash,
  isLoading,
  onAddAccount,
  onEditAccount,
}) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-5 sm:p-6 border border-[#E0E6ED] shadow-2xs space-y-4 animate-pulse">
        {/* Header Skeleton */}
        <div className="space-y-1.5">
          <div className="h-5 w-32 bg-gray-200 rounded-md" />
          <div className="h-3 w-56 bg-gray-100 rounded-md" />
        </div>

        <div className="border-t border-[#E0E6ED] -mx-5" />

        {/* Account Rows Skeleton */}
        <div className="divide-y divide-[#E0E6ED]">
          {[1, 2].map((i) => (
            <div key={i} className="py-4 first:pt-0 last:pb-0 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-11 h-11 rounded-2xl bg-gray-100 shrink-0" />
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-gray-200 rounded w-28" />
                  <div className="h-3 bg-gray-100 rounded w-44" />
                  <div className="flex items-center gap-2">
                    <div className="w-24 sm:w-32 h-1.5 bg-gray-100 rounded-full" />
                    <div className="w-6 h-2 bg-gray-100 rounded" />
                  </div>
                </div>
              </div>
              <div className="space-y-1.5 text-right shrink-0">
                <div className="h-4 bg-gray-200 rounded w-24 ml-auto" />
                <div className="h-3 bg-gray-100 rounded w-16 ml-auto" />
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-[#E0E6ED] -mx-5" />

        {/* Footer Skeleton */}
        <div className="flex items-center justify-between pt-1">
          <div className="h-4 bg-gray-200 rounded w-32" />
          <div className="h-5 bg-gray-200 rounded w-24" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-5 sm:p-6 border border-[#E0E6ED] shadow-2xs space-y-4">
      {/* 1. Header (Title & Subtitle + "+ Tambah tempat dana" on top right) */}
      <div className="flex items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-base sm:text-lg font-bold text-[#0E1726] leading-tight">
            Posisi dana
          </h2>
        </div>

        {onAddAccount && (
          <button
            type="button"
            onClick={onAddAccount}
            className="text-xs font-semibold text-[#FC480D] hover:underline flex items-center gap-1 cursor-pointer shrink-0"
          >
            <Icon icon="ph:plus-bold" className="w-3.5 h-3.5" />
            <span>Tambah tempat dana</span>
          </button>
        )}
      </div>

      {/* 2. DESKTOP VIEW: Data Table (>= md) */}
      <div className="hidden md:block overflow-x-auto -mx-5 px-5 pt-1">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-gray-100 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
              <th className="py-3 px-4">TEMPAT</th>
              <th className="py-3 px-4 text-center w-28">PENCATATAN</th>
              <th className="py-3 px-4 text-right w-36">SALDO</th>
              <th className="py-3 px-4 text-left w-48">PORSI</th>
              <th className="py-3 px-4 text-right w-32">UPDATE</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-xs">
            {accounts.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-12 text-center text-gray-400">
                  <div className="w-10 h-10 rounded-2xl bg-orange-50 text-[#FC480D] flex items-center justify-center mx-auto mb-2 border border-orange-100/60">
                    <Icon icon="ph:wallet-bold" className="w-5 h-5" />
                  </div>
                  <p className="font-bold text-gray-700 text-sm">Belum ada kantong kas</p>
                  <p className="text-xs text-gray-400 mt-0.5">Kantong kas akan muncul di sini.</p>
                </td>
              </tr>
            ) : (
              accounts.map((acc) => {
                const rawBalance = acc.balance ?? acc.current_balance ?? 0;
                const balance = typeof rawBalance === 'number' && !isNaN(rawBalance) ? rawBalance : Number(rawBalance) || 0;
                const validTotalCash = typeof totalCash === 'number' && !isNaN(totalCash) ? totalCash : Number(totalCash) || 0;
                const percentage =
                  typeof acc.allocation_percentage === 'number' && !isNaN(acc.allocation_percentage)
                    ? Math.round(acc.allocation_percentage)
                    : typeof acc.allocated_percentage === 'number' && !isNaN(acc.allocated_percentage)
                    ? Math.round(acc.allocated_percentage)
                    : validTotalCash > 0
                    ? Math.min(100, Math.max(0, Math.round((balance / validTotalCash) * 100)))
                    : 0;
                const safePercentage = isNaN(percentage) ? 0 : percentage;
                const isSportoWallet = acc.is_sporto_wallet;
                const isKYCWallet = Boolean(
                  acc.is_kyc_wallet ||
                  (acc.notes && acc.notes.toLowerCase().includes('kyc'))
                );
                const isLocked = isSportoWallet || isKYCWallet;
                const isWallet = acc.is_sporto_wallet || acc.pocket_type === 'ewallet' || acc.pocket_type === 'sporto_wallet';
                const isBank = acc.pocket_type === 'rekening';

                return (
                  <tr key={acc.id} className="hover:bg-gray-50/70 transition-colors">
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                            isWallet
                              ? 'bg-orange-50 text-[#FC480D]'
                              : isBank
                              ? 'bg-blue-50 text-blue-600'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          <Icon
                            icon={
                              isWallet
                                ? 'ph:wallet-bold'
                                : isBank
                                ? 'ph:bank-bold'
                                : 'ph:money-bold'
                            }
                            className="w-4.5 h-4.5"
                          />
                        </div>
                        <div>
                          <div className="font-bold text-gray-900 text-sm leading-snug">{acc.name}</div>
                          <div className="text-gray-400 text-xs mt-0.5">
                            {acc.notes || (isWallet ? 'Saldo komunitas · settlement H+1' : 'Rekening kas komunitas')}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      {isWallet ? (
                        <span className="inline-block text-[11px] font-semibold text-blue-600 bg-blue-50 px-2.5 py-0.5 rounded-full">
                          Otomatis
                        </span>
                      ) : (
                        <span className="inline-block text-[11px] font-semibold text-gray-600 bg-gray-100 px-2.5 py-0.5 rounded-full">
                          Manual
                        </span>
                      )}
                    </td>
                    <td className="py-3.5 px-4 text-right font-bold text-sm text-gray-900 whitespace-nowrap">
                      {formatRupiahPlain(balance)}
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden shrink-0">
                          <div
                            className="h-full bg-[#FC480D] rounded-full transition-all duration-300"
                            style={{ width: `${safePercentage}%` }}
                          />
                        </div>
                        <span className="text-xs font-semibold text-gray-500 whitespace-nowrap">
                          {safePercentage}%
                        </span>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-2 text-xs text-gray-400 whitespace-nowrap">
                        <span>{formatShortDateTime(acc.updated_at)}</span>
                        {isLocked ? (
                          <span
                            className="text-gray-300"
                            title={
                              isSportoWallet
                                ? 'Dompet Sporto otomatis (tidak dapat diedit)'
                                : 'Rekening KYC komunitas (tidak dapat diedit)'
                            }
                          >
                            <Icon icon="ph:lock-simple" className="w-3.5 h-3.5" />
                          </span>
                        ) : onEditAccount ? (
                          <button
                            type="button"
                            onClick={() => onEditAccount(acc)}
                            className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#FC480D] transition-colors cursor-pointer"
                            title="Edit kantong kas"
                          >
                            <Icon icon="ph:pencil-simple-bold" className="w-3.5 h-3.5" />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {accounts.length > 0 && (
            <tfoot>
              <tr className="border-t border-gray-200">
                <td colSpan={2} className="py-3.5 px-4 font-bold text-xs text-[#0E1726]">
                  Total kas komunitas
                </td>
                <td className="py-3.5 px-4 text-right font-extrabold text-sm text-[#0E1726] whitespace-nowrap">
                  {formatRupiahPlain(totalCash)}
                </td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* 3. MOBILE VIEW: Compact List (< md) */}
      <div className="md:hidden">
        <div className="border-t border-[#E0E6ED] -mx-4 sm:-mx-5 my-2" />

        <div className="divide-y divide-[#E0E6ED]">
          {accounts.length === 0 ? (
            <div className="text-center py-8 px-4">
              <div className="w-12 h-12 rounded-2xl bg-orange-50 text-[#FC480D] flex items-center justify-center mx-auto mb-3 border border-orange-100/60">
                <Icon icon="ph:wallet-bold" className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-bold text-[#0E1726]">Belum ada kantong kas</h3>
              <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto leading-relaxed">
                Kantong kas seperti Dompet Sporto dan rekening kas komunitas akan muncul di sini.
              </p>
            </div>
          ) : (
            accounts.map((acc) => {
              const rawBalance = acc.balance ?? acc.current_balance ?? 0;
              const balance = typeof rawBalance === 'number' && !isNaN(rawBalance) ? rawBalance : Number(rawBalance) || 0;
              const validTotalCash = typeof totalCash === 'number' && !isNaN(totalCash) ? totalCash : Number(totalCash) || 0;

              const percentage =
                typeof acc.allocation_percentage === 'number' && !isNaN(acc.allocation_percentage)
                  ? Math.round(acc.allocation_percentage)
                  : typeof acc.allocated_percentage === 'number' && !isNaN(acc.allocated_percentage)
                  ? Math.round(acc.allocated_percentage)
                  : validTotalCash > 0
                  ? Math.min(100, Math.max(0, Math.round((balance / validTotalCash) * 100)))
                  : 0;

              const safePercentage = isNaN(percentage) ? 0 : percentage;
              const isSportoWallet = acc.is_sporto_wallet;
              const isKYCWallet = Boolean(
                acc.is_kyc_wallet ||
                (acc.notes && acc.notes.toLowerCase().includes('kyc'))
              );
              const isLocked = isSportoWallet || isKYCWallet;
              const isWallet = acc.is_sporto_wallet || acc.pocket_type === 'ewallet' || acc.pocket_type === 'sporto_wallet';
              const isBank = acc.pocket_type === 'rekening';

              return (
                <div key={acc.id} className="py-3.5 first:pt-0 last:pb-0 flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div
                      className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${
                        isWallet
                          ? 'bg-orange-50 text-[#FC480D]'
                          : isBank
                          ? 'bg-blue-50 text-blue-600'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      <Icon
                        icon={
                          isWallet
                            ? 'ph:wallet-bold'
                            : isBank
                            ? 'ph:bank-bold'
                            : 'ph:money-bold'
                        }
                        className="w-5 h-5"
                      />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <h4 className="text-sm font-bold text-[#0E1726] truncate">
                          {acc.name}
                        </h4>
                        {isWallet && (
                          <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full shrink-0">
                            Otomatis
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">
                        {acc.notes || (isWallet ? 'Saldo komunitas · settlement H+1' : 'Rekening kas')}
                      </p>

                      <div className="flex items-center gap-2 mt-2">
                        <div className="w-20 sm:w-28 h-1.5 bg-gray-100 rounded-full overflow-hidden shrink-0">
                          <div
                            className="h-full bg-[#FC480D] rounded-full transition-all duration-300"
                            style={{ width: `${safePercentage}%` }}
                          />
                        </div>
                        <span className="text-[11px] font-semibold text-gray-400">
                          {safePercentage}%
                        </span>

                        {isLocked ? (
                          <span
                            className="text-gray-300 ml-0.5"
                            title={
                              isSportoWallet
                                ? 'Dompet Sporto otomatis (tidak dapat diedit)'
                                : 'Rekening KYC komunitas (tidak dapat diedit)'
                            }
                          >
                            <Icon icon="ph:lock-simple" className="w-3.5 h-3.5" />
                          </span>
                        ) : onEditAccount ? (
                          <button
                            type="button"
                            onClick={() => onEditAccount(acc)}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md hover:bg-gray-100 text-[11px] font-semibold text-gray-400 hover:text-[#FC480D] transition-colors cursor-pointer active:scale-95"
                          >
                            <Icon icon="ph:pencil-simple-bold" className="w-3 h-3" />
                            <span>Edit</span>
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <span className="text-sm sm:text-base font-bold text-[#0E1726] block tracking-tight">
                      {formatRupiahPlain(balance)}
                    </span>
                    <span className="text-[10px] text-gray-400 block mt-0.5">
                      {formatShortDateTime(acc.updated_at)}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {accounts.length > 0 && (
          <>
            <div className="border-t border-[#E0E6ED] -mx-4 sm:-mx-5 my-3" />
            <div className="flex items-center justify-between pt-1">
              <span className="text-xs font-bold text-gray-500">Total kas komunitas</span>
              <span className="text-base font-bold text-[#0E1726] tracking-tight">
                {formatRupiahPlain(totalCash)}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
